import { Spin } from 'antd'
import { useRolodexRulers } from 'features/fde/rulers/rulerApi'
import { Ruler } from 'features/fde/rulers/rulerTypes'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_FCA_ML,
  LABEL_LMSD,
  LABEL_R_T,
  LABEL_TC,
  LABEL_TMM,
  LABEL_TMM_MINUS_FCA,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import {
  msdThresholdMm,
  rulerLengthMm,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRemoteness'
import { useFfsStrategy } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import {
  formatGteThreshold,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import minBy from 'lodash/minBy'
import React, { FC, useMemo } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

export interface FfsFlawSizeCriteraSectionProps {
  loading: boolean
  flawSizeCheckResult: Omit<FlawSizeCriteriaCheckResult, 'isLoaing'>
}

const TOOLTIP_LMSD = (
  <>
    Distance to the nearest major structural discontinuity
    <TooltipDivider />
    {LABEL_LMSD} ≥ 1.8 × sqrt(D × t<Sub>c</Sub>)
  </>
)

const TOOLTIP_R_T = (
  <>
    Remaining Thickness Ratio
    <TooltipDivider />({LABEL_TMM} - {LABEL_FCA_ML}) / {LABEL_TC}
  </>
)

interface FlawSizeCriteriaProps {
  tmm: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  indicationId: string | undefined
  tc: GeckoMeasurement | null
  d: GeckoMeasurement | null
}

const WORKING_UNIT = GeckoMeasurementUnit.Millimeters
const withWorkingUnit: (val: number) => GeckoMeasurement = (val) =>
  GeckoMeasurement.CreateFromValue(val, WORKING_UNIT)

type FlawSizeCriteria = 'remainingThicknessRatio' | 'min_thickness' | 'lmsd'

export type FlawSizeCriteriaCheckResult = {
  isLoading: boolean
  criteriaValues: Record<FlawSizeCriteria, GeckoMeasurement | null>
  failingCriteria: ReadonlySet<FlawSizeCriteria>
  lmsdThreshold: GeckoMeasurement | null
  lossThreshold: GeckoMeasurement
  remainingThicknessThreshold: GeckoMeasurement | null
}

/**
 * Both of these now delegate to `ffsResultPublish/ffsRemoteness.ts`, which owns
 * Eq. 5.10 for the pre-planning remoteness check too. Behaviour is unchanged —
 * these wrappers only re-wrap the mm numbers as `GeckoMeasurement`s.
 */
function getRulerLength(ruler: Ruler): GeckoMeasurement {
  return withWorkingUnit(rulerLengthMm(ruler))
}

function getLmsdThreshold(
  d: GeckoMeasurement | null,
  tc: GeckoMeasurement | null
): GeckoMeasurement | null {
  const thresholdMm = msdThresholdMm({
    dMm: d?.convertTo(WORKING_UNIT).num,
    tcMm: tc?.convertTo(WORKING_UNIT).num,
  })
  return isNil(thresholdMm) ? null : withWorkingUnit(thresholdMm)
}

export function useLevelOneFlawSizeCriteriaCheck({
  tmm,
  fcaMl,
  fca,
  tc,
  d,
  indicationId,
}: FlawSizeCriteriaProps): FlawSizeCriteriaCheckResult {
  const {
    strategy: {
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()

  const { rulers, isLoading: rulersLoading } = useRolodexRulers(
    useMemo(
      () => (indicationId ? { indication: indicationId } : undefined),
      [indicationId]
    )
  )

  // Default remaining thickness threshold ratio of 0.2
  const remainingThicknessThreshold = useMemo(
    () => GeckoMeasurement.CreateFromValue(0.2, GeckoMeasurementUnit.Ratio),
    []
  )

  const rt = useMemo(
    () =>
      isNil(tmm) || isNil(fcaMl) || isNil(tc) || isNil(fca)
        ? null
        : GeckoMeasurement.CreateFromValue(
            (tmm.convertTo(WORKING_UNIT_THICKNESS).num -
              fcaMl.convertTo(WORKING_UNIT_THICKNESS).num) /
              tc.convertTo(WORKING_UNIT_THICKNESS).num,
            GeckoMeasurementUnit.Ratio
          ),
    [tc, fca, fcaMl, tmm]
  )

  return useMemo(() => {
    const lossThreshold = criteriaThicknessThreshold
    const lmsdThreshold = getLmsdThreshold(d, tc)
    const criteriaValues = {
      remainingThicknessRatio: rt,
      min_thickness:
        isNil(tmm) || isNil(fcaMl)
          ? null
          : withWorkingUnit(
              tmm.convertTo(WORKING_UNIT).num -
                fcaMl?.convertTo(WORKING_UNIT).num
            ),
      lmsd: minBy(rulers?.map(getRulerLength), (meas) => meas.num) ?? null,
    }

    const failingCriteria = new Set<FlawSizeCriteria>()

    if (
      isNil(criteriaValues.remainingThicknessRatio) ||
      criteriaValues.remainingThicknessRatio.num <
        remainingThicknessThreshold.num
    ) {
      failingCriteria.add('remainingThicknessRatio')
    }
    if (
      isNil(criteriaValues.min_thickness) ||
      isNil(lossThreshold) ||
      criteriaValues.min_thickness.convertTo(GeckoMeasurementUnit.Millimeters)
        .num < lossThreshold?.num
    ) {
      failingCriteria.add('min_thickness')
    }
    if (
      isNil(criteriaValues.lmsd) ||
      isNil(lmsdThreshold) ||
      criteriaValues.lmsd.num < lmsdThreshold.num
    ) {
      failingCriteria.add('lmsd')
    }

    return {
      isLoading: rulersLoading,
      criteriaValues,
      failingCriteria,
      lmsdThreshold,
      lossThreshold,
      remainingThicknessThreshold,
    }
  }, [
    d,
    fcaMl,
    rt,
    rulers,
    rulersLoading,
    tc,
    tmm,
    criteriaThicknessThreshold,
    remainingThicknessThreshold,
  ])
}

const TooltipContentTmmFcaLimit: FC<{
  lossThreshold: GeckoMeasurement
}> = ({ lossThreshold }) => {
  const { formatMeasurement } = useGeckoLocale()
  return (
    <>
      {LABEL_TMM} - {LABEL_FCA_ML} {'≥'}{' '}
      <span>{formatMeasurement({ measurement: lossThreshold })}</span>
    </>
  )
}

const RawFfsFlawSizeCriteriaSection: FC<FfsFlawSizeCriteraSectionProps> = ({
  loading,
  flawSizeCheckResult,
}) => {
  const {
    criteriaValues: flawSizeCriteriaCheckValues,
    failingCriteria: flawSizeFailingCritera,
    lossThreshold,
    lmsdThreshold,
    remainingThicknessThreshold,
  } = flawSizeCheckResult
  const {
    strategy: {
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_R_T}
          labelTooltip={TOOLTIP_R_T}
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.remainingThicknessRatio}
          measurementType='ratio'
          isValid={!flawSizeFailingCritera.has('remainingThicknessRatio')}
          trailingText={formatGteThreshold(remainingThicknessThreshold)}
        />
        <FfsParameterField
          label={LABEL_TMM_MINUS_FCA}
          labelTooltip={
            <TooltipContentTmmFcaLimit
              lossThreshold={criteriaThicknessThreshold}
            />
          }
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.min_thickness}
          isValid={!flawSizeFailingCritera.has('min_thickness')}
          trailingText={(value) =>
            formatGteThreshold(lossThreshold, { unit: value?.unit })
          }
        />
        <FfsParameterField
          label={LABEL_LMSD}
          labelTooltip={TOOLTIP_LMSD}
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.lmsd}
          isValid={!flawSizeFailingCritera.has('lmsd')}
          trailingText={(value) =>
            formatGteThreshold(lmsdThreshold, {
              unit: value?.unit ?? WORKING_UNIT,
            })
          }
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsFlawSizeCriteriaSection = React.memo(
  RawFfsFlawSizeCriteriaSection
)
